/**
 * Cart JS
 *    This script contains all cart related js functionality
 */

// Events
const EVT_CART_UPDATED = "cart:updated";
const EVT_CART_FINISH = "cart:finished";
const EVT_CART_PENDING = "cart:pending";

// Classes
const CLASS_HIDDEN = "is-hidden";
const CLASS_SCROLL_DISABLED = "is-scroll-disabled";
const CLASS_DISABLED = "is-disabled";

// Selectors
const SEL_CART_CONTAINER = "[data-cart-container]";
const SEL_CHECKOUT_BUTTON = "[data-checkout-button]";

const SEL_CART_DRAWER = "[data-cart-drawer]";
const SEL_CART_DRAWER_TOGGLE = "[data-cart-drawer-toggle]";

const SEL_QUANTITY_SELECTOR = "[data-quantity-selector]";

const SEL_PLUS = "[data-plus]";
const SEL_MINUS = "[data-minus]";
const SEL_INPUT = "[data-input]";
const SEL_ITEM_REMOVE = "[data-item-remove]";

// ATTR
const ATTR_VARIANT_ID = "data-variant-id";

/**
 * Cart
 */

class Cart {
  constructor() {
    this.container = null;
    this.quantitySelectors = [];

    document.addEventListener(EVT_CART_PENDING, this.onCartPending.bind(this));
  }

  async fetchCartContents() {
    if (!this.container) return;
    fetch('/?sections=cart-contents')
      .then(response => response.json())
      .then(data => {
        const contents = data['cart-contents'];
        const temp = document.createElement('div');
        temp.innerHTML = contents;
        const content = temp.firstElementChild;
        const innerContent = content.innerHTML;

        this.container.innerHTML = innerContent;
        document.dispatchEvent(new Event(EVT_CART_FINISH));
      })
      .catch(err => console.error('Failed to update cart drawer:', err));
  }

  initQuantitySelector() {
    if (!this.container) return;
    const quantitySelectors = Array.from(this.container.querySelectorAll(SEL_QUANTITY_SELECTOR));

    quantitySelectors.forEach(element => {
      this.quantitySelectors.push(new QuantitySelector(element));
    })
  }

  initItemRemoveButton() {
    if (!this.container) return;
    const removeButtons = Array.from(this.container.querySelectorAll(SEL_ITEM_REMOVE));
    removeButtons.forEach(e => e.addEventListener("click", this.onRemoveItem.bind(this)));
  }

  disableCheckout() {
    const checkout = this.container.querySelector(SEL_CHECKOUT_BUTTON);
    if (checkout) checkout.classList.add(CLASS_DISABLED);
  }

  disableItemRemove() {
    if (!this.container) return;
    const removeButtons = Array.from(this.container.querySelectorAll(SEL_ITEM_REMOVE));
    removeButtons.forEach(e => e.disabled = true);
  }

  updateCart(variant_id, quantity) {
    if (!variant_id) return;
    document.dispatchEvent(new Event(EVT_CART_PENDING));

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: {
          [variant_id]: quantity
        }
      })
    })
    .then(res => res.json())
    .then(cart => {
      document.dispatchEvent(new CustomEvent(EVT_CART_UPDATED, { detail: cart }));
    })
    .catch(err => console.error('Error updating cart:', err));
  }

  onRemoveItem(e) {
    const targetEl = e.currentTarget;
    const variantId = targetEl.getAttribute(ATTR_VARIANT_ID);
    
    if (!variantId) return;

    this.updateCart(variantId, 0);
  }

  onCartPending() {
    this.quantitySelectors.forEach(qty => qty.disableSelector());
    this.disableCheckout();
    this.disableItemRemove();
  }
}

/**
 * Cart Drawer
 */

class CartDrawer extends Cart {
  constructor() {
    super();
    this.container = document.querySelector(SEL_CART_DRAWER);
    this.toggles = Array.from(document.querySelectorAll(SEL_CART_DRAWER_TOGGLE));

    this.init();
  }

  async init() {
    document.addEventListener(EVT_CART_UPDATED, this.onCartUpdated.bind(this));
    document.addEventListener(EVT_CART_FINISH, this.onCartFinish.bind(this));
    this.toggles.forEach(e => e.addEventListener("click", this.toggle.bind(this)));
    await this.fetchCartContents();
  }

  attachToggleEvent() {
    const drawerToggle = this.container.querySelector(SEL_CART_DRAWER_TOGGLE);
    drawerToggle?.addEventListener("click", this.toggle.bind(this));
  }
  
  toggle(e) {
    e.preventDefault();
    this.container.classList.toggle(CLASS_HIDDEN);
    const isHidden = this.container.classList.contains(CLASS_HIDDEN);
    
    document.body.classList.toggle(CLASS_SCROLL_DISABLED, !isHidden);
  }

  open() {
    this.container.classList.remove(CLASS_HIDDEN);
    document.body.classList.add(CLASS_SCROLL_DISABLED);
  }

  onCartFinish() {
    this.attachToggleEvent();
    this.initQuantitySelector();
    this.initItemRemoveButton();
  }

  async onCartUpdated() {
    await this.fetchCartContents();
    this.open();
  }
}

/**
 * Quantity Selector
 */

class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.plusButton = container.querySelector(SEL_PLUS);
    this.minusButton = container.querySelector(SEL_MINUS);
    this.input = container.querySelector(SEL_INPUT);
    this.timeout = null;
    this.variantId = this.input?.getAttribute(ATTR_VARIANT_ID);
    
    this.attachEvents();
  }

  attachEvents() {
    this.plusButton.addEventListener("click", this.onPlusClick.bind(this));
    this.minusButton.addEventListener("click", this.onMinusClick.bind(this));
    this.input.addEventListener("input", this.onInputChange.bind(this));
  }

  get minQuantity() {
    return this.input ? parseInt(this.input.getAttribute("min")) : 1;
  }

  get quantity() {
    return this.input ? parseInt(this.input.value) : 1;
  }

  set quantity(value) {
    this.input.value = value;
  }

  disableSelector() {
    this.plusButton.disabled = true;
    this.minusButton.disabled = true;
    this.input.disabled = true;
  }

  updateCart() {
    if (!this.variantId) return;
    if (this.timeout) clearTimeout(this.timeout);
    
    this.timeout = setTimeout(() => {
      document.dispatchEvent(new Event(EVT_CART_PENDING));

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [this.variantId]: this.quantity
          }
        })
      })
      .then(res => res.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent(EVT_CART_UPDATED, { detail: cart }));
      })
      .catch(err => console.error('Error updating cart:', err));
    }, 500);
  }
  
  // Events

  onPlusClick() {
    this.quantity = this.quantity + 1;
    this.updateCart();
  }

  onMinusClick() {
    const newQuantity = this.quantity > 1 ? this.quantity - 1 : 1;
    if (newQuantity == this.quantity) return;
    this.quantity = newQuantity;
    this.updateCart();
  }

  onInputChange() {
    this.updateCart();
  }
}

// Initialize cart
document.addEventListener('DOMContentLoaded', function () {
  new CartDrawer;
});